import { createPhoto } from 'api-client';
import { removePhotoFromCache } from 'common/infrastructure/photo-management/upload.cache';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import {
  MutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';

export interface UploadPhotoToInspectionVars {
  photoId: string;
  base64Data: string;
  inspectionId: string;
  fileName: string;
  tenantId: string;
}

/**
 * Repository for inspection photos.
 *
 * Unlike assets and work tasks, an inspection does not exist on the server until
 * it is submitted, and there is no separate link/unlink endpoint — photos are
 * linked by including their IDs in the submit-inspection payload. So the upload
 * here is just `createPhoto` (entity-agnostic); the link happens at submit time.
 *
 * The mutation is registered globally (see remoteState/mutations.ts) so a paused
 * (offline) upload survives reload and resumes via resumePausedMutations. Because
 * the photoId is generated client-side, it can be added to the submit payload
 * immediately, even while the upload is still pending.
 */
const InspectionPhotoRepository = {
  getUploadPhotoToInspectionMutationConfig(): MutationOptions {
    return {
      mutationKey: ['uploadPhotoToInspection'],
      ...defaultMutationOptions,
      retry: 3,
      mutationFn: async ({
        photoId,
        base64Data,
        fileName,
        tenantId,
      }: UploadPhotoToInspectionVars) => {
        await createPhoto(tenantId, photoId, { filename: fileName, data: base64Data, tenantId });
      },
      onMutate: () => undefined,
      onSuccess: async (_: unknown, { inspectionId, photoId }: UploadPhotoToInspectionVars) => {
        await removePhotoFromCache(inspectionId, photoId);
      },
      onError: (err: unknown) => {
        loggerService.log(LoggerLevel.ERROR, 'Photo upload to inspection failed', err);
      },
      onSettled: () => undefined,
    };
  },
};

export default InspectionPhotoRepository;
